export const CHALK_HISTORY_INDEX_KEY = "__chalkHistoryIndex";

type IndexedHistoryState = Record<string, unknown> & {
  [CHALK_HISTORY_INDEX_KEY]?: number;
};

const indexedState = (state: unknown, index: number): IndexedHistoryState => ({
  ...(typeof state === "object" && state != null ? state : {}),
  [CHALK_HISTORY_INDEX_KEY]: index,
});

export const getHistoryEntryIndex = (state: unknown = window.history.state): number | undefined => {
  if (typeof state !== "object" || state == null) return undefined;
  const index = (state as IndexedHistoryState)[CHALK_HISTORY_INDEX_KEY];
  return typeof index === "number" ? index : undefined;
};

export const ensureCurrentHistoryEntryIndex = (): number => {
  const existingIndex = getHistoryEntryIndex();
  if (existingIndex != null) return existingIndex;
  window.history.replaceState(indexedState(window.history.state, 0), "", window.location.href);
  return 0;
};

/** Adds a stable position to Next.js history state so navigation guards can restore either Back or
 * Forward without guessing. Next preserves the extra field when it writes its own route state. */
export const installHistoryEntryIndexTracking = (): (() => void) => {
  let currentIndex = ensureCurrentHistoryEntryIndex();
  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  const trackedPushState: History["pushState"] = function (this: History, data, unused, url) {
    currentIndex++;
    return originalPushState.call(this, indexedState(data, currentIndex), unused, url);
  };
  const trackedReplaceState: History["replaceState"] = function (this: History, data, unused, url) {
    return originalReplaceState.call(this, indexedState(data, currentIndex), unused, url);
  };
  const handlePopState = (event: PopStateEvent) => {
    const targetIndex = getHistoryEntryIndex(event.state);
    if (targetIndex != null) currentIndex = targetIndex;
  };

  window.history.pushState = trackedPushState;
  window.history.replaceState = trackedReplaceState;
  window.addEventListener("popstate", handlePopState);
  return () => {
    window.removeEventListener("popstate", handlePopState);
    if (window.history.pushState === trackedPushState) window.history.pushState = originalPushState;
    if (window.history.replaceState === trackedReplaceState)
      window.history.replaceState = originalReplaceState;
  };
};
